// CalendarPicker.js
import { useAlert } from "components/UI_components/Alert";
import { add } from "date-fns";
import React, { useState, useRef, useEffect, useMemo } from "react";

const CalendarPicker = ({
  startDate,
  disabled = false,
  endDate,
  onDateChange,
  placeholder = "Select dates",
  minDate,
  maxDate,
  subtasks = [],
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const dropdownRef = useRef(null);
  const { addAlert } = useAlert();
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

  // Close calendar when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Helper: parse date strings like "2025-10-2" or ISO strings robustly
  const parseDateString = (s) => {
    if (!s) return null;
    if (s instanceof Date) return new Date(s);
    // Try splitting by '-' or '/'
    const dashParts = String(s).split("-");
    if (dashParts.length === 3) {
      // support YYYY-M-D and YYYY-MM-DD
      const y = parseInt(dashParts[0], 10);
      const m = parseInt(dashParts[1], 10) - 1;
      const d = parseInt(dashParts[2], 10);
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) return new Date(y, m, d);
    }
    const slashParts = String(s).split("/");
    if (slashParts.length === 3) {
      const [m, d, y] = slashParts.map((x) => parseInt(x, 10));
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) return new Date(y, m - 1, d);
    }
    // fallback to Date constructor (handles ISO)
    const dt = new Date(s);
    return isNaN(dt.getTime()) ? null : dt;
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${months[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
  };

  const getDisplayText = () => {
    if (startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    if (startDate) {
      return formatDate(startDate);
    }
    return placeholder;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.toDateString() === d2.toDateString();
  };

  const isInRange = (date) => {
    if (!tempStartDate || !tempEndDate || !date) return false;
    const d = new Date(date);
    const start = new Date(tempStartDate);
    const end = new Date(tempEndDate);
    return d >= start && d <= end;
  };

  const handleDateClick = (date) => {
    if (
      minDate &&
      date > new Date(minDate) &&
      maxDate &&
      date < new Date(maxDate)
    ) {
      return;
    }
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Starting new selection
      setTempStartDate(date);
      setTempEndDate(null);
    } else if (tempStartDate && !tempEndDate) {
      // Completing the range
      if (date >= tempStartDate) {
        if (!minDate || !maxDate) {
          setTempEndDate(date);
          return;
        }
        if (tempStartDate <= new Date(minDate) && date >= new Date(maxDate)) {
          setTempEndDate(date);
        } else {
          setTempStartDate(date);
          setTempEndDate(null);
          return;
        }
      } else {
        if (!minDate || !maxDate) {
          setTempEndDate(tempStartDate);
          setTempStartDate(date);
          return;
        }
        if (date <= new Date(minDate) && tempStartDate >= new Date(maxDate)) {
          setTempStartDate(date);
          setTempEndDate(tempStartDate);
        } else {
          setTempStartDate(date);
          setTempEndDate(null);
          return;
        }
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleOK = () => {
    onDateChange(tempStartDate, tempEndDate);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsOpen(false);
  };

  // Pre-parse subtasks for faster per-cell checks
  const parsedSubtasks = useMemo(() => {
    if (!Array.isArray(subtasks)) return [];
    return subtasks
      .map((s) => {
        const start = parseDateString(s.start_date || s.startDate || s.start);
        const end = parseDateString(s.end_date || s.endDate || s.end);
        return {
          subtask_name: s.subtask_name || s.name || s.title || "Subtask",
          start,
          end,
        };
      })
      .filter((s) => s.start && s.end && s.start <= s.end);
  }, [subtasks]);

  // Check which subtasks cover a given date; return array of names
  const subtasksCoveringDate = (date) => {
    if (!date) return [];
    const d = new Date(date);
    return parsedSubtasks
      .filter((st) => {
        const start = new Date(st.start);
        const end = new Date(st.end);
        // include endpoints
        return d >= start && d <= end;
      })
      .map((st) => st.subtask_name);
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
          }
        }}
        className={`rounded p-0 ${
          compact ? "mt-0.5" : "mt-2"
        } flex items-center justify-between cursor-pointer hover:border-gray-400`}
      >
        <span
          className={`${
            compact ? "text-sm" : "text-base"
          } text-[#343C6A] truncate font-medium`}
        >
          {getDisplayText()}
        </span>
        {!disabled && (
          <svg
            className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-gray-400`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-80">
          {/* Header with month navigation */}
          <div className="flex items-center justify-between p-3 border-b">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <h3 className="font-medium">
              {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>

            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Days of week header */}
          <div className="grid grid-cols-7 gap-1 p-3 pb-2">
            {daysOfWeek.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-medium text-gray-500 py-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1 px-3 pb-3">
            {days.map((date, index) => {
              if (!date) {
                return <div key={index} className="h-8"></div>;
              }
              const isDisabled =
                minDate &&
                date > new Date(minDate) &&
                maxDate &&
                date < new Date(maxDate);

              const isStart = isSameDay(date, tempStartDate);
              const isEnd = isSameDay(date, tempEndDate);
              const inRange = isInRange(date);
              const isToday = isSameDay(date, new Date());

              const covers = subtasksCoveringDate(date); // array of strings

              let cellClass =
                "h-8 flex items-center justify-center text-sm cursor-pointer relative";

              if (isStart || isEnd) {
                cellClass += " bg-[#65558F] z-50 text-white rounded-full ";
              } else if (inRange) {
                cellClass += " bg-[#E8DEF8] text-[#3B0069] rounded -mx-4 ";
              } else if (isToday) {
                cellClass += " bg-gray-100 rounded font-medium ";
              } else {
                cellClass += isDisabled
                  ? " text-gray-400 cursor-not-allowed"
                  : " hover:bg-gray-100 rounded";
              }

              return (
                <div
                  key={index}
                  className={cellClass}
                  onClick={() => handleDateClick(date)}
                >
                  <div className="z-10">{date.getDate()}</div>

                  {/* Subtask indicator: small dots at bottom */}
                  {covers.length > 0 && (
                    // limit to 3 dots for visual clarity; tooltip shows names
                    <div
                      className="absolute bottom-1 left-1 right-1 flex items-center justify-center space-x-1"
                      title={covers.join(", ")}
                    >
                      {covers.slice(0, 3).map((name, i) => (
                        <span
                          key={i}
                          className="w-1.5 h-1.5 rounded-full"
                          // choose simple palette based on index
                          style={{
                            backgroundColor:
                              i === 0
                                ? "#34D399"
                                : i === 1
                                ? "#60A5FA"
                                : "#F59E0B",
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer buttons */}
          <div className="flex justify-end space-x-2 p-3 border-t">
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleOK}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPicker;
